import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { ChatThreadList } from '../components/chat/ChatThreadList';
import { ChatConversation } from '../components/chat/ChatConversation';
import useChatStream from '../hooks/useChatStream';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { MessageSquarePlus, MessagesSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MyMessages = () => {
  const { user, loading: authLoading } = useAuth();
  const [threads, setThreads] = useState([]);
  const [targets, setTargets] = useState({ business: { label: 'Business (Office)' }, parties: [] });
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [dest, setDest] = useState('business');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const fetchThreads = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/threads`, { withCredentials: true });
      setThreads(res.data.threads || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  const fetchTargets = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/message-targets`, { withCredentials: true });
      setTargets(res.data || { business: { label: 'Business (Office)' }, parties: [] });
    } catch { /* noop */ }
  }, []);

  useEffect(() => { fetchThreads(); fetchTargets(); }, [fetchThreads, fetchTargets]);

  useChatStream(useCallback(() => { fetchThreads(); }, [fetchThreads]));

  // Build destination options
  const destOptions = [{ value: 'business', label: 'Business (Office)' }];
  (targets.parties || []).forEach((p) => {
    destOptions.push({ value: `host:${p.booking_id}`, label: `${p.host_name} — ${p.child_name || 'party'}'s host` });
    destOptions.push({ value: `both:${p.booking_id}`, label: `Both — Business + ${p.child_name || 'party'}'s host` });
  });

  const sendCompose = async () => {
    const text = body.trim();
    if (!text) { toast.error('Type a message'); return; }
    let target = 'business';
    let booking_id = null;
    if (dest.startsWith('host:')) { target = 'host'; booking_id = dest.slice(5); }
    else if (dest.startsWith('both:')) { target = 'both'; booking_id = dest.slice(5); }
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/customer/threads/send`,
        { target, booking_id, body: text }, { withCredentials: true });
      toast.success('Message sent');
      setComposeOpen(false); setBody(''); setDest('business');
      await fetchThreads();
      const firstThread = res.data.sent?.[0]?.thread_id;
      if (firstThread) setActive(firstThread);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  if (authLoading) return null;

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <div className="container mx-auto px-4 py-6 max-w-6xl" data-testid="my-messages">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Messages</h1>
            <p className="text-sm text-[#6B6B7A] mt-0.5">Chat with the business and your party host.</p>
          </div>
          <Button onClick={() => setComposeOpen(true)} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="new-message-btn">
            <MessageSquarePlus className="w-4 h-4 mr-1.5" /> New message
          </Button>
        </div>

        <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden shadow-sm grid grid-cols-1 md:grid-cols-[320px_1fr] h-[70vh]">
          {/* Thread list (hidden on mobile when a thread is open) */}
          <div className={`border-r border-[#E8E5DF] overflow-y-auto ${active ? 'hidden md:block' : 'block'}`}>
            {loading ? (
              <div className="flex items-center justify-center py-16 text-[#6B6B7A]"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : (
              <ChatThreadList
                threads={threads}
                activeThreadId={active}
                onSelect={(t) => setActive(t.thread_id)}
                emptyText="No conversations yet. Tap 'New message' to start."
              />
            )}
          </div>
          {/* Conversation */}
          <div className={`${active ? 'block' : 'hidden md:block'} min-h-0`}>
            {active ? (
              <ChatConversation
                apiBase="customer"
                threadId={active}
                onBack={() => setActive(null)}
                onActivity={fetchThreads}
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-8 text-[#6B6B7A]">
                <MessagesSquare className="w-10 h-10 mb-3 text-[#E8E5DF]" />
                <p className="text-sm">Select a conversation or start a new message.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compose dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="compose-dialog">
          <DialogHeader><DialogTitle>New message</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#1E1E24]">Send to</label>
              <Select value={dest} onValueChange={setDest}>
                <SelectTrigger className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="compose-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {destOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(targets.parties || []).length === 0 && (
                <p className="text-[11px] text-[#6B6B7A] mt-1">A "Party Host" option appears once a host is assigned to your party.</p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1E1E24]">Message</label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Type your message…" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="compose-body" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setComposeOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={sendCompose} disabled={sending || !body.trim()} className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white" data-testid="compose-send">
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyMessages;
